import { useMemo } from 'react';

import { REVIEWER_ROLES, ROLENAMES } from 'constants/roles';

import type { Roles } from 'models/user';

import { useFeatureAccessControl } from './useFeatureAccessControl';

interface UserRolesProps {
  roles: Roles[];
  isApprovalFlowEnabled?: boolean;
}

interface RoleOption {
  label: string;
  value: string;
  disabled?: boolean;
}

export const useRolesOptions = (props: UserRolesProps): RoleOption[] => {
  const { roles, isApprovalFlowEnabled } = props;
  const rolesOptions = useMemo(() => {
    if (!isApprovalFlowEnabled) {
      return roles
        .filter((role: Roles) => !REVIEWER_ROLES.includes(role.name))
        .map((role: Roles) => ({
          label: role.name.charAt(0).toUpperCase() + role.name.slice(1),
          value: role.name
        }));
    } else {
      return roles.map((role: Roles) => ({
        label:
          ROLENAMES[role.name] ||
          role.name.charAt(0).toUpperCase() + role.name.slice(1),
        value: role.name
      }));
    }
  }, [roles, isApprovalFlowEnabled]);

  return rolesOptions;
};

type UseSafeRoleOptionsProps = UserRolesProps & {
  currentRole: string;
  unavailableLabel?: string;
};

export const useSafeRoleOptions = ({
  roles,
  currentRole,
  unavailableLabel = 'Current Role (Unavailable)'
}: UseSafeRoleOptionsProps) => {
  const {
    isBatchApprovalRequired,
    isBatchesPageEnabled,
    isPayoutDraftApprovalFlowEnabled
  } = useFeatureAccessControl();

  const isApprovalFlowEnabled =
    (isBatchesPageEnabled && isBatchApprovalRequired) ||
    isPayoutDraftApprovalFlowEnabled;

  const options = useRolesOptions({ roles, isApprovalFlowEnabled });

  const isCurrentRoleAssignable = options.some((o) => o.value === currentRole);

  const safeOptions = useMemo(() => {
    if (isCurrentRoleAssignable) return options;

    return [
      ...options,
      { label: unavailableLabel, value: currentRole, disabled: true }
    ];
  }, [options, isCurrentRoleAssignable, currentRole, unavailableLabel]);

  return { options: safeOptions, isCurrentRoleAssignable };
};
